import { useEffect, useRef } from 'react'
import { useApolloClient } from '@graphcommerce/graphql'
import { useCustomerSession } from '@graphcommerce/magento-customer/hooks'
import { CustomerTokenDocument } from '@graphcommerce/magento-customer/hooks/CustomerToken.gql'
import { useRouter } from 'next/router'

const TOKEN_EXPIRATION_MS = 60 * 60 * 1000 // 60 minutes

/**
 * Hook that automatically logs out users when their token expires
 * This prevents the "appears logged in but isn't" state
 */
export function useAutoLogoutOnExpiration() {
  const session = useCustomerSession()
  const client = useApolloClient()
  const router = useRouter()
  const timeoutRef = useRef<NodeJS.Timeout>()
  const hasShownWarningRef = useRef(false)

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // If user is logged in, set up expiration handling
    if (session.loggedIn && session.createdAt) {
      const createdAt = new Date(session.createdAt).getTime()
      const now = new Date().getTime()
      const timeUntilExpiration = TOKEN_EXPIRATION_MS - (now - createdAt)

      if (timeUntilExpiration > 0) {
        // Set timeout to clear token when it expires
        timeoutRef.current = setTimeout(() => {
          // Clear the token from cache
          client.writeQuery({
            query: CustomerTokenDocument,
            data: {
              customerToken: {
                __typename: 'CustomerToken',
                token: null,
                createdAt: new Date('2000').toUTCString(),
                valid: false,
              },
            },
            broadcast: true,
          })

          // If user is on a protected page, redirect to signin
          const protectedPaths = ['/account', '/checkout', '/cart']
          const currentPath = router.asPath
          const isOnProtectedPage = protectedPaths.some((path) => currentPath.startsWith(path))
          
          if (isOnProtectedPage && currentPath !== '/account/signin') {
            router.push('/account/signin')
          }
          
          // For product pages, just let the page re-render with new session state
          // The ProductPageLoginToBuyButton will appear automatically

          hasShownWarningRef.current = false
        }, timeUntilExpiration)
      } else {
        // Token is already expired, clear it immediately
        if (session.token) {
          client.writeQuery({
            query: CustomerTokenDocument,
            data: {
              customerToken: {
                __typename: 'CustomerToken',
                token: null,
                createdAt: new Date('2000').toUTCString(),
                valid: false,
              },
            },
            broadcast: true,
          })
        }
      }
    } else {
      // User logged out, reset warning flag
      hasShownWarningRef.current = false
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [session.loggedIn, session.token, session.createdAt, client, router])
}
